import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useKeycloak } from '@/contexts/KeycloakContext';
import api from '@/services/api';
import {
  isPushSupported,
  getPermissionStatus,
  subscribeToPush,
  unsubscribeFromPush,
  isSubscribed,
  sendTestNotification,
} from '@/services/pushNotifications';

interface GoogleAccount {
  googleId: string;
  email: string;
  name?: string;
  picture?: string;
  label?: string;
  isPrimary: boolean;
  lastSyncedAt?: string;
}

interface PendingInvite {
  id: string;
  targetEmail: string;
  label: string;
  expiresAt: string;
  createdAt: string;
}

interface UserPreferences {
  aiClassificationEnabled: boolean;
  aiAvailable: boolean;
  calendarIntegrationEnabled: boolean;
  healthKeywords: string[];
  emailDigestFrequency: 'realtime' | 'daily' | 'weekly' | 'never';
  undoDelaySeconds: 0 | 5 | 10 | 20 | 30;
}

const UNDO_DELAY_OPTIONS = [
  { value: 0, label: 'Off' },
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 20, label: '20s' },
  { value: 30, label: '30s' },
] as const;

export function Settings() {
  const { user, logout } = useKeycloak();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<GoogleAccount[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Preferences state
  const [preferences, setPreferences] = useState<UserPreferences>({
    aiClassificationEnabled: false,
    aiAvailable: false,
    calendarIntegrationEnabled: false,
    healthKeywords: [],
    emailDigestFrequency: 'daily',
    undoDelaySeconds: 10,
  });
  const [healthKeywordInput, setHealthKeywordInput] = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLabel, setInviteLabel] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  // Export and deletion state
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Account nickname editing
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editingNickname, setEditingNickname] = useState('');

  // Push notification state
  const [pushSupported] = useState(isPushSupported());
  const [pushPermission, setPushPermission] = useState(getPermissionStatus());
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushTesting, setPushTesting] = useState(false);

  // Handle OAuth callback messages
  useEffect(() => {
    const googleConnected = searchParams.get('google_connected');
    const googleError = searchParams.get('google_error');
    const inviteError = searchParams.get('invite_error');

    if (googleConnected) {
      setMessage({ type: 'success', text: `Connected ${googleConnected}` });
      searchParams.delete('google_connected');
      setSearchParams(searchParams, { replace: true });
      fetchAccounts();
      fetchInvites();
    } else if (googleError) {
      setMessage({ type: 'error', text: `Connection failed: ${googleError}` });
      searchParams.delete('google_error');
      setSearchParams(searchParams, { replace: true });
    } else if (inviteError) {
      const errorMessages: Record<string, string> = {
        invalid_or_expired: 'This invite link is invalid or has expired',
        expired: 'This invite has expired',
        server_error: 'Something went wrong. Please try again.',
      };
      setMessage({ type: 'error', text: errorMessages[inviteError] || inviteError });
      searchParams.delete('invite_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await api.get<{ accounts: GoogleAccount[] }>('/email/accounts');
      setAccounts(response.data.accounts);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvites = async () => {
    try {
      const response = await api.get<{ invites: PendingInvite[] }>('/auth/google/invites');
      setPendingInvites(response.data.invites);
    } catch (err) {
      console.error('Failed to fetch invites:', err);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await api.get<{ preferences: UserPreferences }>('/settings');
      setPreferences(response.data.preferences);
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    }
  };

  const updatePreference = async (key: keyof UserPreferences, value: unknown) => {
    try {
      setSavingPrefs(true);
      await api.patch('/settings/preferences', { [key]: value });
      setPreferences(prev => ({ ...prev, [key]: value }));
      setMessage({ type: 'success', text: 'Setting saved' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const addHealthKeyword = () => {
    const keyword = healthKeywordInput.trim().toLowerCase();
    if (keyword && !preferences.healthKeywords.includes(keyword)) {
      const newKeywords = [...preferences.healthKeywords, keyword];
      updatePreference('healthKeywords', newKeywords);
      setHealthKeywordInput('');
    }
  };

  const removeHealthKeyword = (keyword: string) => {
    const newKeywords = preferences.healthKeywords.filter(k => k !== keyword);
    updatePreference('healthKeywords', newKeywords);
  };

  useEffect(() => {
    fetchAccounts();
    fetchInvites();
    fetchPreferences();
    checkPushSubscription();
  }, []);

  const checkPushSubscription = async () => {
    if (pushSupported) {
      const subscribed = await isSubscribed();
      setPushSubscribed(subscribed);
    }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      const success = await subscribeToPush();
      if (success) {
        setPushSubscribed(true);
        setPushPermission('granted');
        setMessage({ type: 'success', text: 'Push notifications enabled' });
      } else {
        setPushPermission(getPermissionStatus());
        setMessage({ type: 'error', text: 'Failed to enable push notifications' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to enable push notifications' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      await unsubscribeFromPush();
      setPushSubscribed(false);
      setMessage({ type: 'success', text: 'Push notifications disabled' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to disable push notifications' });
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    setPushTesting(true);
    try {
      const result = await sendTestNotification();
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to send test notification' });
    } finally {
      setPushTesting(false);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    try {
      setSendingInvite(true);
      await api.post('/auth/google/invite', {
        email: inviteEmail.trim(),
        label: inviteLabel.trim() || undefined,
      });
      setMessage({ type: 'success', text: `Invite sent to ${inviteEmail}` });
      setInviteEmail('');
      setInviteLabel('');
      setShowInviteForm(false);
      fetchInvites();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.message || err.response?.data?.error || 'Failed to send invite' });
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (id: string) => {
    try {
      await api.delete(`/auth/google/invites/${id}`);
      setPendingInvites(pendingInvites.filter(i => i.id !== id));
      setMessage({ type: 'success', text: 'Invite cancelled' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to cancel invite' });
    }
  };

  const handleConnectGoogle = async () => {
    try {
      setConnecting(true);
      const response = await api.get<{ authUrl: string }>('/auth/google/connect');
      window.location.href = response.data.authUrl;
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to start connection' });
      setConnecting(false);
    }
  };

  const handleDisconnect = async (googleId: string) => {
    try {
      await api.delete(`/auth/google/accounts/${googleId}`);
      setAccounts(accounts.filter(a => a.googleId !== googleId));
      setMessage({ type: 'success', text: 'Account disconnected' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to disconnect' });
    }
  };

  const handleSaveNickname = async (googleId: string) => {
    try {
      await api.patch(`/auth/google/accounts/${googleId}`, { label: editingNickname.trim() || null });
      setAccounts(accounts.map(a =>
        a.googleId === googleId ? { ...a, label: editingNickname.trim() || undefined } : a
      ));
      setEditingAccountId(null);
      setEditingNickname('');
      setMessage({ type: 'success', text: 'Nickname saved' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save nickname' });
    }
  };

  const startEditingNickname = (account: GoogleAccount) => {
    setEditingAccountId(account.googleId);
    setEditingNickname(account.label || '');
  };

  const handleExportData = async () => {
    try {
      setExporting(true);
      const response = await api.get('/settings/export', { responseType: 'blob' });

      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wholeworm-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setMessage({ type: 'success', text: 'Data exported successfully' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to export data' });
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      await api.delete('/settings/account', { data: { confirm: 'DELETE' } });
      setMessage({ type: 'success', text: 'Account deletion scheduled (72-hour grace period)' });
      setShowDeleteConfirm(false);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete account' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 space-y-6 pb-24">
      <header className="pt-2">
        <h1 className="text-2xl font-bold">Settings</h1>
      </header>

      {/* Message banner */}
      {message && (
        <div
          className={`p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-accent-success/20 text-accent-success'
              : 'bg-accent-danger/20 text-accent-danger'
          }`}
        >
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Profile */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          Profile
        </h2>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-xl font-bold">
            {user?.name?.charAt(0) || '?'}
          </div>
          <div>
            <p className="font-medium">{user?.name}</p>
            <p className="text-white/50 text-sm">{user?.email}</p>
          </div>
        </div>
      </section>

      {/* Connected Accounts */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          Email Accounts
        </h2>

        {loading ? (
          <div className="py-4 text-center text-white/50">Loading...</div>
        ) : (
          <div className="space-y-3">
            {accounts.map((account) => (
              <div key={account.googleId} className="py-3 border-b border-white/5 last:border-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {account.picture ? (
                      <img
                        src={account.picture}
                        alt=""
                        className="w-10 h-10 rounded-full flex-shrink-0"
                      />
                    ) : (
                      <span className="text-2xl flex-shrink-0">📧</span>
                    )}
                    <div className="min-w-0 flex-1">
                      {editingAccountId === account.googleId ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editingNickname}
                            onChange={(e) => setEditingNickname(e.target.value)}
                            placeholder="Nickname (e.g., Work, Personal)"
                            className="flex-1 bg-white/10 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveNickname(account.googleId);
                              if (e.key === 'Escape') setEditingAccountId(null);
                            }}
                          />
                          <button
                            onClick={() => handleSaveNickname(account.googleId)}
                            className="text-accent-primary text-sm px-2"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingAccountId(null)}
                            className="text-white/50 text-sm px-2"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            {account.label && (
                              <span className="text-sm font-medium text-accent-primary">{account.label}</span>
                            )}
                            <p className={`text-sm truncate ${account.label ? 'text-white/60' : 'font-medium'}`}>
                              {account.email}
                            </p>
                            <button
                              onClick={() => startEditingNickname(account)}
                              className="text-white/30 hover:text-white/60 text-xs flex-shrink-0"
                              title="Edit nickname"
                            >
                              ✏️
                            </button>
                          </div>
                          {account.lastSyncedAt && (
                            <p className="text-xs text-white/40">
                              Last synced {new Date(account.lastSyncedAt).toLocaleDateString()}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  {editingAccountId !== account.googleId && (
                    <button
                      onClick={() => handleDisconnect(account.googleId)}
                      className="text-sm text-accent-danger py-2 px-3 flex-shrink-0"
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <div className="border-t border-white/10 mt-2 pt-4">
                <p className="text-xs text-white/40 mb-2">Pending Invites</p>
                {pendingInvites.map((invite) => (
                  <div key={invite.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">⏳</span>
                      <div>
                        <p className="text-sm">{invite.targetEmail}</p>
                        <p className="text-xs text-white/40">
                          Expires {new Date(invite.expiresAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleCancelInvite(invite.id)}
                      className="text-sm text-white/50 py-2 px-3"
                    >
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add account options */}
            <div className="border-t border-white/10 mt-2 pt-4 space-y-3">
              {/* Same browser connect (for already logged-in Google) */}
              <button
                onClick={handleConnectGoogle}
                disabled={connecting}
                className="flex items-center gap-3 py-2 w-full text-left"
              >
                <span className="text-xl">🔗</span>
                <div>
                  <span className="text-accent-primary">
                    {connecting ? 'Connecting...' : 'Connect Google Account'}
                  </span>
                  <p className="text-xs text-white/40">Use current browser session</p>
                </div>
              </button>

              {/* Invite by email (for different Chrome profile) */}
              {!showInviteForm ? (
                <button
                  onClick={() => setShowInviteForm(true)}
                  className="flex items-center gap-3 py-2 w-full text-left"
                >
                  <span className="text-xl">📧</span>
                  <div>
                    <span className="text-accent-primary">Invite by Email</span>
                    <p className="text-xs text-white/40">For accounts in other Chrome profiles</p>
                  </div>
                </button>
              ) : (
                <form onSubmit={handleSendInvite} className="bg-white/5 rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-sm text-white/50 block mb-1">
                      Email address to invite
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="work@company.com"
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                      required
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/50 block mb-1">
                      Label (optional)
                    </label>
                    <input
                      type="text"
                      value={inviteLabel}
                      onChange={(e) => setInviteLabel(e.target.value)}
                      placeholder="e.g., Work, Personal"
                      className="w-full bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      disabled={sendingInvite || !inviteEmail.trim()}
                      className="btn-primary flex-1 py-2 text-sm"
                    >
                      {sendingInvite ? 'Sending...' : 'Send Invite'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteForm(false);
                        setInviteEmail('');
                        setInviteLabel('');
                      }}
                      className="btn-ghost py-2 px-4 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </section>

      {/* AI & Intelligence */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          AI & Intelligence
        </h2>
        <div className="space-y-4">
          {/* AI Classification Toggle */}
          <div className="flex items-center justify-between py-2">
            <div className="flex-1">
              <span className="font-medium">AI Email Classification</span>
              <p className="text-xs text-white/40 mt-0.5">
                Use Claude AI to better categorize and prioritize emails
              </p>
            </div>
            <button
              onClick={() => updatePreference('aiClassificationEnabled', !preferences.aiClassificationEnabled)}
              disabled={savingPrefs || !preferences.aiAvailable}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                preferences.aiClassificationEnabled && preferences.aiAvailable
                  ? 'bg-accent-primary'
                  : 'bg-white/20'
              } ${!preferences.aiAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  preferences.aiClassificationEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {!preferences.aiAvailable && (
            <p className="text-xs text-white/30 -mt-2">AI service not available on this instance</p>
          )}

          {/* Calendar Integration Toggle */}
          <div className="flex items-center justify-between py-2 border-t border-white/10 pt-4">
            <div className="flex-1">
              <span className="font-medium">Calendar Awareness</span>
              <p className="text-xs text-white/40 mt-0.5">
                Adjust email priority based on your calendar busyness
              </p>
            </div>
            <button
              onClick={() => updatePreference('calendarIntegrationEnabled', !preferences.calendarIntegrationEnabled)}
              disabled={savingPrefs || accounts.length === 0}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                preferences.calendarIntegrationEnabled
                  ? 'bg-accent-primary'
                  : 'bg-white/20'
              } ${accounts.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div
                className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  preferences.calendarIntegrationEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {accounts.length === 0 && (
            <p className="text-xs text-white/30 -mt-2">Connect a Google account to enable</p>
          )}

          {/* Health Keywords */}
          <div className="border-t border-white/10 pt-4">
            <div className="mb-2">
              <span className="font-medium">Health Keywords</span>
              <p className="text-xs text-white/40 mt-0.5">
                Emails containing these keywords get higher priority
              </p>
            </div>
            <div className="flex gap-2 flex-wrap mb-2">
              {preferences.healthKeywords.map(keyword => (
                <span
                  key={keyword}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-accent-primary/20 text-accent-primary rounded text-sm"
                >
                  {keyword}
                  <button
                    onClick={() => removeHealthKeyword(keyword)}
                    className="hover:text-white"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={healthKeywordInput}
                onChange={(e) => setHealthKeywordInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHealthKeyword())}
                placeholder="e.g., pharmacy, medication, doctor"
                className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
              />
              <button
                onClick={addHealthKeyword}
                disabled={!healthKeywordInput.trim()}
                className="btn-primary py-2 px-4 text-sm"
              >
                Add
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          Notifications
        </h2>
        <div className="space-y-3">
          {!pushSupported ? (
            <div className="py-2 text-white/50 text-sm">
              Push notifications are not supported on this device
            </div>
          ) : pushPermission === 'denied' ? (
            <div className="py-2">
              <p className="text-white/50 text-sm">
                Notifications are blocked. Please enable them in your browser settings.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between py-2">
                <div>
                  <span>Push Notifications</span>
                  <p className="text-xs text-white/40">
                    {pushSubscribed ? 'Receiving notifications' : 'Get notified about important updates'}
                  </p>
                </div>
                <button
                  onClick={pushSubscribed ? handleDisablePush : handleEnablePush}
                  disabled={pushLoading}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    pushSubscribed ? 'bg-accent-primary' : 'bg-white/20'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      pushSubscribed ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {pushSubscribed && (
                <div className="flex items-center justify-between py-2 border-t border-white/10">
                  <span className="text-sm">Test Notification</span>
                  <button
                    onClick={handleTestPush}
                    disabled={pushTesting}
                    className="btn-ghost text-sm py-2 px-3 min-h-0 min-w-0"
                  >
                    {pushTesting ? 'Sending...' : 'Send Test'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Actions & Safety */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          Actions & Safety
        </h2>
        <div className="space-y-3">
          <div className="py-2">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="font-medium">Undo Cushion</span>
                <p className="text-xs text-white/40 mt-0.5">
                  Delay before unsubscribe actions are executed
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {UNDO_DELAY_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updatePreference('undoDelaySeconds', option.value)}
                  disabled={savingPrefs}
                  className={`flex-1 py-2 px-2 rounded-lg text-sm font-medium transition-colors ${
                    preferences.undoDelaySeconds === option.value
                      ? 'bg-accent-primary text-white'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-white/30 mt-2">
              {preferences.undoDelaySeconds === 0
                ? 'Actions execute immediately (no undo)'
                : `You have ${preferences.undoDelaySeconds} seconds to undo after unsubscribing`}
            </p>
          </div>
        </div>
      </section>

      {/* Integrations Status */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          Integrations
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">📧</span>
              <span>Google</span>
            </div>
            <span className={accounts.length > 0 ? 'text-accent-success text-sm' : 'text-white/50 text-sm'}>
              {accounts.length > 0 ? `${accounts.length} connected` : 'Not connected'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">📅</span>
              <span>Calendar</span>
            </div>
            <span className={accounts.length > 0 && preferences.calendarIntegrationEnabled ? 'text-accent-success text-sm' : 'text-white/50 text-sm'}>
              {accounts.length > 0 && preferences.calendarIntegrationEnabled ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <span className="text-xl">🤖</span>
              <span>AI</span>
            </div>
            <span className={preferences.aiAvailable && preferences.aiClassificationEnabled ? 'text-accent-success text-sm' : 'text-white/50 text-sm'}>
              {preferences.aiAvailable ? (preferences.aiClassificationEnabled ? 'Active' : 'Disabled') : 'Not available'}
            </span>
          </div>
        </div>
      </section>

      {/* Data */}
      <section className="card">
        <h2 className="text-sm font-medium text-white/50 mb-3 uppercase tracking-wide">
          Data & Privacy
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <span>Data Region</span>
            <span className="text-white/50 text-sm">Canada (PIPEDA)</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <span>Export Data</span>
              <p className="text-xs text-white/40">Download all your data</p>
            </div>
            <button
              onClick={handleExportData}
              disabled={exporting}
              className="btn-ghost text-sm py-2 px-3 min-h-0 min-w-0"
            >
              {exporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-white/10 pt-4">
            <div>
              <span className="text-accent-danger">Delete Account</span>
              <p className="text-xs text-white/40">72-hour grace period to cancel</p>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="text-sm py-2 px-3 text-accent-danger hover:bg-accent-danger/10 rounded-lg"
            >
              Delete
            </button>
          </div>
        </div>
      </section>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-accent-danger mb-2">Delete Account?</h3>
            <p className="text-white/70 text-sm mb-4">
              Your account will be scheduled for deletion. You have 72 hours to cancel.
              After that, all your data will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 btn-ghost py-2"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 py-2 px-4 bg-accent-danger text-white rounded-lg font-medium"
              >
                {deleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sign Out */}
      <button
        onClick={() => logout()}
        className="w-full py-4 text-center text-accent-danger font-medium"
      >
        Sign Out
      </button>
    </div>
  );
}
